import Button from 'antd/es/button'
import Drawer from 'antd/es/drawer'
import Form from 'antd/es/form'
import Layout from 'antd/es/layout'
import Menu from 'antd/es/menu'
import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { useAppSelector } from '../../redux/hooks.ts'
import {
  selectTheme,
  selectThemeStyle,
} from '../../features/setting/setting.slice.ts'
import { Theme } from '../../features/setting/setting.def.ts'
import logoHorizontalLightDev from '@assets/logos/ideya/ideya-logo-light-horizontal-dev.png'
import logoHorizontalDarkDev from '@assets/logos/ideya/ideya-logo-dark-horizontal-dev.png'
import logoHorizontalLight from '@assets/logos/ideya/ideya-logo-light-horizontal.png'
import logoHorizontalDark from '@assets/logos/ideya/ideya-logo-dark-horizontal.png'
import logoIconLightDev from '@assets/logos/ideya/ideya-logo-emblem-light-dev.png'
import logoIconDarkDev from '@assets/logos/ideya/ideya-logo-emblem-dark-dev.png'
import logoIconLight from '@assets/logos/ideya/ideya-logo-emblem-light.png'
import logoIconDark from '@assets/logos/ideya/ideya-logo-emblem-dark.png'
//import ipponLogoIconLight from '@assets/logos/ippon/ippon-logo.png'
import DashboardLight from '@assets/icons/navigation/dashboard-light.svg'
import DashboardDark from '@assets/icons/navigation/dashboard-dark.svg'
import IdeaListLight from '@assets/icons/navigation/idea-list-light.svg'
import IdeaListDark from '@assets/icons/navigation/idea-list-dark.svg'
import RankingLight from '@assets/icons/navigation/ranking-light.svg'
import RankingDark from '@assets/icons/navigation/ranking-dark.svg'
import AccountDark from '@assets/icons/navigation/account-dark.svg'
import AccountLight from '@assets/icons/navigation/account-light.svg'
import TickerDark from '@assets/icons/navigation/ticker-dark.svg'
import TickerLight from '@assets/icons/navigation/ticker-light.svg'
import { useLocation, useNavigate } from 'react-router-dom'
import { allPages } from '../../router/routes.tsx'
import { hasAccess } from '../../utils/access-permission.ts'
import { UserInfo } from '../../apis/auth/auth.def.ts'
import { HeaderItems } from './components/header.tsx'
import { MenuOutlined, PlusOutlined } from '@ant-design/icons'
import { useFixedBreakpoint } from '../hooks/use-is-mobile.ts'
import { ActionIdeaModal } from '../../pages/idea-list/components/idea-modal/idea-action/action-idea-modal'
import { IdeyaButton } from '../antd/ideya-button/ideya-button.tsx'
import { CreateIdeaFormFieldTypes } from '../../pages/idea-list/components/idea-modal/idea-action/action-idea-form.tsx'
import {
  canCreateIdeaPermissions,
  hasPermissionAndFirmCheck,
} from '../../utils/permissions.ts'
import { isProd, isIppon } from '../../utils/helpers.ts'
import { useTranslation } from 'react-i18next'
import { GROUP_ROLE } from '../../router/roles.ts'
import { NEW_RELIC, newRelic } from '../../utils/new-relic.ts'
import { useTutorial } from '../../contexts/use-tutorial.ts'
import { NewFeatureModal } from '../../pages/components/release-note/new-feature-modal.tsx'
import { getLocalStorageItem } from '../../utils/local-storage.ts'

const { Header, Sider, Content } = Layout

interface PageProps {
  userInfo: UserInfo
  children: ReactNode
}

export const Page: React.FC<PageProps> = (props) => {
  const [collapsed, setCollapsed] = useState(false)
  const { children, userInfo } = props
  const colorTheme = useAppSelector(selectTheme)
  const isDarkTheme = colorTheme === Theme.Dark
  const themeStyle = useAppSelector(selectThemeStyle)
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const screens = useFixedBreakpoint()
  const isMobile = !screens.md
  const [showCreateIdeaModal, setShowCreateIdeaModal] = useState(false)
  const [editIdeaId, setEditIdeaId] = useState<string | null>(null)
  const [form] = Form.useForm<CreateIdeaFormFieldTypes>()
  const { t } = useTranslation()
  const { setRef } = useTutorial()
  const createIdeaRef = useRef(null)
  const releaseNoteVersion = import.meta.env.VITE_RELEASE_NOTE_VERSION as string
  const [openNewFeatureModal, setOpenNewFeatureModal] = useState<boolean>(
    () => {
      const releaseNoteData = getLocalStorageItem(releaseNoteVersion) as
        { show?: boolean } | undefined

      return releaseNoteData?.show ?? true
    }
  )

  useEffect(() => {
    setRef('createIdea', createIdeaRef)

    return () => setRef('createIdea', null) // Cleanup when unmounting
  }, [setRef])

  const location = useLocation()
  const MenuItems = [
    {
      key: 'dashboard',
      icon: isDarkTheme ? <DashboardDark /> : <DashboardLight />,
      label: t('menu.dashboard'),
    },
    {
      key: 'idea',
      icon: isDarkTheme ? <IdeaListDark /> : <IdeaListLight />,
      label: t('menu.idea_list'),
    },
    {
      key: 'ranking/overall-ranking',
      label: t('menu.ranking'),
      icon: isDarkTheme ? <RankingDark /> : <RankingLight />,
      //children: [
      //  { key: 'ranking/overall-ranking', label: 'Overall Ranking' },
      //  // isV2Ready { key: 'ranking/my-achievements', label: 'My Achievements' },
      //],
    },
    {
      key: 'statistics',
      label: t('menu.statistics'),
      icon: isDarkTheme ? <DashboardDark /> : <DashboardLight />,
    },
    {
      key: 'account-management',
      label: t('menu.account_management.label'),
      icon: isDarkTheme ? <AccountDark /> : <AccountLight />,
      children: [
        {
          key: 'account-management/firm',
          label: t('menu.account_management.firm'),
        },
        {
          key: 'account-management/user',
          label: t('menu.account_management.user'),
        },
        {
          key: 'account-management/mail-list',
          label: t('menu.account_management.mail_list'),
        },
      ],
    },
    {
      key: 'ticker-management',
      label: t('menu.ticker_management'),
      icon: isDarkTheme ? <TickerDark /> : <TickerLight />,
    },
  ]

  // Hardcoded mapping from route key to translation key
  const routeToTranslationKey: Record<string, string> = {
    dashboard: 'menu.dashboard',
    idea_list: 'menu.idea_list',
    ranking: 'menu.ranking',
    statistics: 'menu.statistics',
    account_management: 'menu.account_management.label',
    ticker_management: 'menu.ticker_management',
  }

  const getLabelFromPathname = (
    pathname: string,
    t: (key: string) => string
  ): string => {
    const routeKey = pathname.substring(1)
    const translationKey = routeToTranslationKey[routeKey]
    return translationKey ? t(translationKey) : t('menu.idea_list')
  }

  const [navigationLabel, setNavigationLabel] = useState(() =>
    getLabelFromPathname(location.pathname, t)
  )

  const canCreateIdea = hasPermissionAndFirmCheck(
    canCreateIdeaPermissions,
    userInfo.firmId,
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )

  const Logo = () => {
    const getLogoSrc = () => {
      if (isIppon) {
        return collapsed
          ? 'https://asset.1ppon.com/fav-icon.svg'
          : 'https://asset.1ppon.com/word-logo.svg'
      }
      // Production logos
      if (isProd()) {
        return isDarkTheme
          ? collapsed
            ? logoIconDark
            : logoHorizontalDark
          : collapsed
            ? logoIconLight
            : logoHorizontalLight
      }
      return isDarkTheme
        ? collapsed
          ? logoIconDarkDev
          : logoHorizontalDarkDev
        : collapsed
          ? logoIconLightDev
          : logoHorizontalLightDev
    }

    return (
      <img
        alt={'logo'}
        src={getLogoSrc()}
        style={{
          height: isIppon ? '70px' : '40px',
          paddingLeft: isMobile
            ? 0
            : collapsed
              ? themeStyle.paddingMDCompact
              : themeStyle.paddingMD,
        }}
      />
    )
  }

  // Dynamically build menu items based on user role permissions
  const externalRoutes = allPages.filter((route) =>
    hasAccess('external', userInfo, route)
  )
  const internalRoutes = allPages.filter((route) =>
    hasAccess('internal', userInfo, route)
  )

  const filteredMenuItems = MenuItems.filter((menuItem) => {
    // Filter out dashboard for Manager group role
    if (
      userInfo.groupRole === GROUP_ROLE.ROLE_MANAGER &&
      menuItem.key === 'dashboard'
    ) {
      return false
    }

    // Check if the menuItem exists in internalRoutes or externalRoutes
    const isInInternalRoutes = internalRoutes.some(
      (route) => route.key === menuItem.key
    )
    const isInExternalRoutes = externalRoutes.some(
      (route) => route.key === menuItem.key
    )

    // If the menuItem has children, filter its children recursively
    if (menuItem.children) {
      menuItem.children = menuItem.children.filter((child) => {
        const isChildInInternalRoutes = internalRoutes.some(
          (route) => route.key === child.key
        )
        const isChildInExternalRoutes = externalRoutes.some(
          (route) => route.key === child.key
        )
        return isChildInInternalRoutes || isChildInExternalRoutes
      })
    }

    return (
      isInInternalRoutes ||
      isInExternalRoutes ||
      (menuItem.children && menuItem.children.length > 0)
    )
  })

  const handleNavigationClick = (key: string) => {
    const item = MenuItems.flatMap((item) =>
      item.children ? item.children : item
    ).find((i) => i.key === key)
    const label = item ? item.label : key
    setNavigationLabel(label)
    navigate(`/${key}`)
    setMobileMenuOpen(false) // close drawer on mobile
  }

  const handleCloseActionIdeaModal = () => {
    form.resetFields()
    setShowCreateIdeaModal(false)
  }

  return (
    <Layout
      style={{
        height: '100lvh',
        minHeight: '100lvh',
        backgroundColor: `var(--background-color)`,
      }}
    >
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={220}
          collapsedWidth={80}
        >
          <div
            style={{
              justifyContent: 'center',
              alignContent: 'center',
              height: '80px',
            }}
          >
            <Logo />
          </div>
          <Menu
            style={{
              height: 'calc(100% - 64px)', // leave space for button
            }}
            mode={'inline'}
            selectedKeys={[location.pathname.substring(1)]}
            onClick={({ key }) => {
              handleNavigationClick(key)
            }}
            items={filteredMenuItems}
            inlineCollapsed={collapsed}
          />
          {canCreateIdea && (
            <div
              ref={createIdeaRef}
              style={{
                position: 'absolute',
                bottom: 24 + 44, // 44px for footer height
                left: 0,
                width: '100%',
                padding: collapsed ? '0 8px' : '0 20px',
                display: 'flex',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <IdeyaButton
                type={'primary'}
                block
                icon={<PlusOutlined />}
                onClick={() => {
                  newRelic.addPageAction(NEW_RELIC.PAGE_ACTION.clickCreateIdea)
                  setShowCreateIdeaModal(true)
                }}
                style={{
                  width: collapsed ? 42 : '',
                  height: collapsed ? 42 : '',
                  borderRadius: '40px',
                  border: `1px solid ${themeStyle.createIdeaBorderColor}`,
                  background: themeStyle.createIdeaBgColor,
                  boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.15)',
                  color: themeStyle.createIdeaTextColor,
                  fontWeight: 400,
                  fontSize: themeStyle.fontSizeLG,
                }}
                className={'remove-compact'}
              >
                {!collapsed && t('common.create_idea')}
              </IdeyaButton>
            </div>
          )}
        </Sider>
      )}
      <Drawer
        placement={'left'}
        closable={false}
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        width={220}
        style={{ zIndex: 10000, padding: 0 }}
      >
        <div
          style={{
            justifyContent: 'center',
            alignContent: 'center',
            height: '80px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Logo />
        </div>
        <Menu
          mode={'inline'}
          onClick={({ key }) => {
            handleNavigationClick(key)
          }}
          items={filteredMenuItems}
          style={{ height: 'calc(100% - 64px)' }}
        />
        {canCreateIdea && (
          <div
            style={{
              position: 'absolute',
              bottom: 24 + 44,
              left: 0,
              width: '100%',
              padding: collapsed ? '0 8px' : '0 20px',
              display: 'flex',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <IdeyaButton
              type={'primary'}
              block
              icon={<span style={{ fontWeight: 700, fontSize: 18 }}>+</span>}
              onClick={() => {
                setMobileMenuOpen(false)
                setShowCreateIdeaModal(true)
                newRelic.addPageAction(NEW_RELIC.PAGE_ACTION.clickCreateIdea)
              }}
              style={{
                borderRadius: '40px',
                boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.15)',
                fontWeight: 400,
                fontSize: themeStyle.fontSizeLG,
                border: `1px solid ${themeStyle.createIdeaBorderColor}`,
                background: themeStyle.createIdeaBgColor,
                color: themeStyle.createIdeaTextColor,
              }}
              className={'remove-compact'}
            >
              {t('common.create_idea')}
            </IdeyaButton>
          </div>
        )}
      </Drawer>
      <Layout style={{ height: '100lvh', background: 'transparent' }}>
        <Header
          style={{
            padding: '0px',
            height: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          {isMobile ? (
            <Button
              type={'text'}
              icon={<MenuOutlined style={{ fontSize: 22 }} />}
              onClick={() => setMobileMenuOpen(true)}
              style={{
                marginTop: 8,
                marginLeft: 8,
                color: themeStyle.naviText,
                background: 'transparent',
                border: 'none',
                boxShadow: 'none',
              }}
            />
          ) : (
            <HeaderItems
              userInfo={userInfo}
              collapsed={collapsed}
              onCollapseClicked={() => setCollapsed(!collapsed)}
              navigationLabel={navigationLabel}
            />
          )}
          {/* On mobile, show user dropdown on right */}
          {isMobile && (
            <div style={{ marginRight: 8 }}>
              <HeaderItems
                userInfo={userInfo}
                collapsed={collapsed}
                onCollapseClicked={() => setCollapsed(!collapsed)}
                navigationLabel={navigationLabel}
                mobile
              />
            </div>
          )}
        </Header>
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            height: 'calc(100lvh - 44px - 44px)',
            background: 'transparent',
            padding: isMobile
              ? `${themeStyle.marginLG} 20px`
              : `${themeStyle.marginLG} ${themeStyle.marginXL}`,
            paddingBottom: isMobile
              ? `max(16px, env(safe-area-inset-bottom))`
              : themeStyle.marginLG,
          }}
        >
          <Content
            className={'page'}
            style={{
              minHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              background: 'transparent',
              padding: 0,
            }}
          >
            {children}
          </Content>
        </div>
      </Layout>
      <ActionIdeaModal
        showActionModal={showCreateIdeaModal}
        showLiquidateModal={false}
        setShowLiquidatedModal={() => {}}
        form={form}
        handleCloseModal={handleCloseActionIdeaModal}
        onIdeaActioned={() => {
          setShowCreateIdeaModal(false)
          window.dispatchEvent(new Event('refresh-idea-list'))
        }}
        editIdeaId={editIdeaId}
        setEditIdeaId={setEditIdeaId}
      />
      <NewFeatureModal
        open={openNewFeatureModal}
        closeModal={() => setOpenNewFeatureModal(false)}
      />
    </Layout>
  )
}
